/**
 * ImpersonateConfirmModal (#1137 Task 4.1).
 *
 * Reusable confirm modal for both AdminPanel (platform scope) and
 * EnterpriseAdmin (workspace scope, Task 4.2). The Confirm button is
 * disabled for the first 200ms after mount — a deliberate friction
 * pause so admins don't impersonate someone with a single mis-aimed
 * click.
 *
 * The modal owns nothing but the enable timer and the rendered prompt.
 * The parent decides what happens on Confirm (typically: POST to a
 * grant endpoint, then call startImpersonation()) and on Cancel
 * (typically: clear the target state).
 */
import React, { useEffect, useState } from 'react';

export default function ImpersonateConfirmModal({ target, onCancel, onConfirm, scope = 'platform' }) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setEnabled(true), 200);
    return () => clearTimeout(id);
  }, []);

  if (!target) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center"
      data-testid="impersonate-modal"
    >
      <div className="bg-[#1E1E1E] border border-[#A61E17] p-6 max-w-md w-full mx-4">
        <h3 className="text-[#F5F1E8] uppercase text-lg font-bold mb-2 font-['Barlow_Condensed']">
          Impersonate {target.email}?
        </h3>
        <p className="text-[#F5F1E8] text-sm mb-4 font-['Inter']">
          You will act as them with full read/write access in their{' '}
          {scope === 'workspace' ? 'workspace' : 'platform'} scope.
          All actions are logged to your account in the audit trail.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            data-testid="impersonate-cancel"
            className="px-4 py-2 text-sm uppercase border border-[#F5F1E8]/40 text-[#F5F1E8] hover:bg-[#F5F1E8]/10 font-['Barlow_Condensed']"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!enabled}
            data-testid="impersonate-confirm"
            className="px-4 py-2 text-sm uppercase font-bold bg-[#A61E17] text-[#F5F1E8] disabled:opacity-50 disabled:cursor-not-allowed font-['Barlow_Condensed']"
          >
            {enabled ? 'Confirm' : 'Wait...'}
          </button>
        </div>
      </div>
    </div>
  );
}
